import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import moment from 'moment';
import { useFormik, Form, FormikProvider } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import {
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  Paper
} from '@material-ui/core';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { v4 as uuidv4 } from 'uuid';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import SaveIcon from '@material-ui/icons/Save';
import Controls from '../../components/controls/Controls';
import {
  baseUrl,
  userDept,
  userStatuses,
  employers,
  Country,
  userType
} from '../../utils/constants';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    border: '1px solid rgba(0, 0, 0, .125)',
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 0
    }
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary
  },
  color: {
    backgroundColor: '#f5f5f5'
  },
  newButton: {
    position: 'absolute',
    right: '10px'
  },
  spacing: {
    marginBottom: theme.spacing(2)
  }
}));

export default function EditUser() {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: 'success' });
  const [status, setStatus] = useState('');
  const [dept, setDept] = useState('');
  const [expanded, setExpanded] = React.useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [users, setUsers] = useState({
    userName: '',
    password: '',
    countryRefId: 1,
    firstName: '',
    surname: '',
    idNumber: '',
    emailAddress: '',
    cellNumber: '',
    status: 1,
    lastLogIn: '',
    passwordPolicy: 0,
    province: 11,
    lastPasswordChange: '',
    systemLanguage: 0,
    userType: 2,
    passwordToken: '',
    description: '',
    supervisorRefId: 0,
    extension: '',
    oldRefId: 0,
    dept: 1
  });

  const navigate = useNavigate();
  const { refId } = useParams(); // Get the refId from the URL

  const UserSchema = Yup.object().shape({
    userName: Yup.string().required('User Name is required'),
    password: Yup.string().required('Password is required'),
    firstName: Yup.string().required('First Name is required'),
    emailAddress: Yup.string().email('Invalid email').required('Email Address is required'),
    status: Yup.number().required('Status is required'),
    countryRefId: Yup.number().required('Country Reference ID is required')
  });

  const now = new Date().toISOString();

  const formik = useFormik({
    initialValues: users,
    enableReinitialize: true,
    validationSchema: UserSchema,
    onSubmit: (values) => {
      console.log('User data:', values);
      saveUser(values);
    }
  });

  // Fetch user data based on refId and populate the form
  useEffect(() => {
    if (refId) {
      fetchUserDetails(refId);
    }
  }, [refId]);

  if (users.lastLogIn) users.lastLogIn = moment(users.lastLogIn).format('yyyy-MM-DD');
  if (users.lastPasswordChange)
    users.lastPasswordChange = moment(users.lastPasswordChange).format('yyyy-MM-DD');

  const fetchUserDetails = (refId) => {
    setLoading(true);
    fetch(`${baseUrl}/api/UserManagement/GetUser?userRefId=${refId}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(
        (data) => {
          console.log('Fetched user data:', data);

          const userData = data || {};

          console.log('Sample user data:', userData.userName);
          setLoading(false);
          setStatus(userData.status || 0);
          setDept(userData.dept || 0);

          setUsers(userData);
        },
        (error) => {
          notifyPopup(`Error fetching user data: ${error.message}`, 'error');
          setLoading(false);
        }
      )
      .catch((error) => {
        notifyPopup(`Error fetching user data: ${error.message}`, 'error');
        setLoading(false);
      });
  };

  const saveUser = (user) => {
    console.log('Payload to be sent:', JSON.stringify(user));
    setLoading(true);
    fetch(`${baseUrl}/api/ServiceAuthentication/UpdateUserDetail`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(user)
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(
        (result) => {
          notifyPopup('User Saved Successfully!', 'success');
          setLoading(false);
          navigate('/settings/configusers');
        },
        (error) => {
          notifyPopup(`Error in Saving User!: ${error.message}`, 'error');
          setLoading(false);
        }
      )
      .catch((error) => {
        notifyPopup(`Error in Saving User!: ${error.message}`, 'error');
        setLoading(false);
      });
  };

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const notifyPopup = (notifyMessage, notifyType) => {
    setNotify({
      isOpen: true,
      message: notifyMessage,
      type: notifyType
    });
  };

  return (
    <Container>
      <Card>
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
              <Typography variant="subtitle1" gutterBottom>
                Edit User
              </Typography>
              <Grid container spacing={2} justifyContent="center" alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Controls.Input name="userName" label="User Name" autoComplete="new-password" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controls.Input
                    name="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controls.Input name="firstName" label="First Name" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controls.Input name="surname" label="Surname" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Controls.Input name="idNumber" label="ID Number" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Controls.Input name="emailAddress" label="Email Address" type="email" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Controls.Input name="cellNumber" label="Cell Number" />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel id="statusLabel">Status</InputLabel>
                    <Select
                      id="status"
                      value={status}
                      name="status"
                      fullWidth
                      size="small"
                      variant="outlined"
                      onChange={(e) => {
                        const { value } = e.target;
                        setStatus(value);
                        formik.setFieldValue('status', value);
                      }}
                    >
                      <MenuItem key="" value="0">
                        Not Selected
                      </MenuItem>
                      {userStatuses.map(({ refId, name }) => (
                        <MenuItem key={refId} value={refId}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel id="deptLabel">Department</InputLabel>
                    <Select
                      id="dept"
                      value={dept}
                      name="dept"
                      fullWidth
                      size="small"
                      variant="outlined"
                      onChange={(e) => {
                        const { value } = e.target;
                        setDept(value);
                        formik.setFieldValue('dept', value);
                      }}
                    >
                      <MenuItem key="" value="0">
                        Not Selected
                      </MenuItem>
                      {userDept.map(({ refId, name }) => (
                        <MenuItem key={refId} value={refId}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Controls.SelectJson
                    name="province"
                    label="Province"
                    defaultValue=""
                    options={employers}
                  />
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Controls.Input name="description" label="Description" />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Controls.SelectJson
                    name="userType"
                    label="userType"
                    defaultValue=""
                    options={userType}
                  />
                </Grid>
                <Grid item xs={12} className={classes.spacing} />
              </Grid>
              <div className={classes.root}>
                <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1bh-content"
                    id="panel1bh-header"
                    className={classes.color}
                  >
                    <Typography className={classes.heading}>Auto Populated Fields</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2} marginTop={2}>
                      {/* <Grid item xs={12} sm={6}>
                        <Paper variant="outlined" margin={10}> */}
                      <Grid item xs={12} sm={4}>
                        <Controls.Input name="extension" label="Extension" />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Controls.Input name="oldRefId" label="Old Reference ID" type="number" />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Controls.DateTime name="lastLogIn" label="Last Login" />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Controls.DateTime name="lastPasswordChange" label="Last Password Change" />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Controls.Input
                          name="systemLanguage"
                          label="System Language"
                          type="number"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Controls.Input
                          name="supervisorRefId"
                          label="Supervisor Reference ID"
                          type="number"
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Controls.Input name="passwordToken" label="Password Token" />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Controls.SelectJson
                          name="countryRefId"
                          label="Country"
                          defaultValue=""
                          options={Country}
                          disabled
                        />
                      </Grid>
                      {/* </Paper>
                      </Grid> */}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </div>
              <Grid container spacing={2} justifyContent="center" alignItems="center">
                <Grid item xs={12} sm={4}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    disabled={loading}
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </Card>
    </Container>
  );
}
